import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { DateTime } from 'luxon';
import './wallet.css';

// Admin read-only view of a client's saved cards (WalletClientDetails). Staff
// can remove a card on the client's request (DELETE /api/cards/admin/:id) — the
// gateway token is never shown. Best-effort: 404 (endpoint not deployed) shows
// an empty state instead of breaking the client page.
//
//   GET    /api/cards/admin/clients/:clientId
//          -> { cards: [ { id, scheme, masked_pan, expiry, cardholder_name,
//               created_at, consent_text_version, consented_at, is_active,
//               deactivated_at } ] }
//   DELETE /api/cards/admin/:id
export default function ClientSavedCardsSection({ clientId }) {
    const [cards, setCards] = useState([]);
    const [loading, setLoading] = useState(true);
    const [removeTarget, setRemoveTarget] = useState(null);
    const [removing, setRemoving] = useState(false);

    const formatDate = (d) => (d ? DateTime.fromISO(d, { zone: 'Asia/Dubai' }).toFormat('dd MMM yy') : '—');
    const last4 = (pan) => String(pan || '').replace(/\D/g, '').slice(-4) || '••••';
    const expiry = (e) => {
        const m = String(e || '').match(/^(\d{4})-(\d{1,2})$/);
        return m ? `${m[2].padStart(2, '0')}/${m[1].slice(-2)}` : (e || '—');
    };

    const fetchCards = useCallback(async () => {
        if (!clientId) return;
        try {
            setLoading(true);
            const res = await axios.get(`/api/cards/admin/clients/${clientId}`);
            setCards(Array.isArray(res.data?.cards) ? res.data.cards : []);
        } catch (err) {
            if (err.response?.status !== 404) console.error('Error loading client cards:', err);
            setCards([]);
        } finally {
            setLoading(false);
        }
    }, [clientId]);

    useEffect(() => { fetchCards(); }, [fetchCards]);

    const handleRemove = async () => {
        if (!removeTarget || removing) return;
        setRemoving(true);
        try {
            await axios.delete(`/api/cards/admin/${removeTarget.id}`);
            toast.success('Card removed.');
            setRemoveTarget(null);
            await fetchCards();
        } catch (err) {
            console.error('Error removing card:', err);
            toast.error(err.response?.data?.error || 'Failed to remove the card.');
        } finally {
            setRemoving(false);
        }
    };

    // Nothing at all (and not loading) → keep the client page clean, hide it.
    if (!loading && cards.length === 0) return null;

    return (
        <div className="swallet__section">
            <div className="swallet__sectionHead">
                <h2 className="swallet__sectionTitle">Saved Cards</h2>
                <span className="swallet__sectionSub">
                    Read-only. Tokens only — no card number is ever stored. Remove on the client&apos;s request.
                </span>
            </div>

            {loading ? (
                <div className="swallet__empty">Loading...</div>
            ) : (
                <table className="swallet__table">
                    <thead>
                        <tr>
                            <th>Card</th>
                            <th>Expiry</th>
                            <th>Added</th>
                            <th>Status</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {cards.map((c) => (
                            <tr key={c.id}>
                                <td style={{ fontWeight: 900 }}>
                                    {(c.scheme || 'Card')} •••• {last4(c.masked_pan)}
                                </td>
                                <td>{expiry(c.expiry)}</td>
                                <td>{formatDate(c.created_at)}</td>
                                <td>
                                    <span className={`swallet__chip ${c.is_active ? 'swallet__chip--green' : 'swallet__chip--red'}`}>
                                        {c.is_active ? 'Active' : 'Removed'}
                                    </span>
                                </td>
                                <td>
                                    {c.is_active ? (
                                        <button className="swallet__btn swallet__btn--danger" onClick={() => setRemoveTarget(c)}>
                                            Remove
                                        </button>
                                    ) : (
                                        <span style={{ color: '#9ca3af', fontSize: 12 }}>
                                            {c.deactivated_at ? `Removed ${formatDate(c.deactivated_at)}` : '—'}
                                        </span>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}

            {removeTarget && (
                <div className="swallet__overlay" onClick={(e) => { if (e.target === e.currentTarget && !removing) setRemoveTarget(null); }}>
                    <div className="swallet__modal">
                        <h3 className="swallet__modalTitle">Remove Saved Card</h3>
                        <p className="swallet__modalText">
                            Remove <strong>{(removeTarget.scheme || 'Card')} •••• {last4(removeTarget.masked_pan)}</strong> for
                            this client? Any bookings set to auto-charge this card will need a new payment method.
                        </p>
                        <div className="swallet__modalActions">
                            <button className="swallet__btn" onClick={() => setRemoveTarget(null)} disabled={removing}>Cancel</button>
                            <button className="swallet__btn swallet__btn--danger" onClick={handleRemove} disabled={removing}>
                                {removing ? 'Removing...' : 'Remove card'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
